import ComposerState from '@intercom/embercom-composer/composer-state';
import TemplatingMixin from '@intercom/embercom-composer/components/mixins/templating';
import Config from '@intercom/embercom-composer/config/config';
import CombinedAttributesMixin from '@intercom/embercom-composer/components/mixins/combined-attributes';
import UploadManager from '@intercom/embercom-composer/lib/upload-manager';
import platform from '@intercom/embercom-composer/lib/platform';
import HtmlCleaner from '@intercom/embercom-composer/lib/html-cleaner';
import DomSelection from '@intercom/embercom-composer/lib/editor/dom-selection';
import { inject as service } from '@ember/service';
import { sanitizeUrl } from '@intercom/pulse/lib/sanitize';
import $ from 'jquery';
import { and, bool, equal, not, or, reads } from '@ember/object/computed';
import { bind, schedule, scheduleOnce } from '@ember/runloop';
import Em from 'ember';
const ComposerActionProxy = Em.Object.extend(Em.ActionHandler, {
  init() {
    this._super(...arguments);
    this.set('target', this.get('composerComponent'));
  },
  willDestroy() {
    this._super(...arguments);
    this.set('target', null);
  },
  sync() {
    this.get('composerComponent').syncEditorView();
  },
  getBlocks() {
    return this.get('composerComponent').getBlocks();
  },
  getHtml() {
    return this.get('composerComponent').getHtml();
  }
});
export default Em.Component.extend(TemplatingMixin, CombinedAttributesMixin, {
  version: '11.11.8',
  blocks: [],
  attributes: [],
  selectedEvents: [],
  admins: [],
  previewContext: null,
  hoverAreaPadding: {
    top: 75,
    right: 75,
    bottom: 75,
    left: 75
  },
  preset: 'default',
  classNames: ['embercom-composer'],
  classNameBindings: ['isDragging:is-dragging', 'isTyping:is-typing', 'isUploading:is-uploading', 'hasImageErrors:has-image-errors', 'isRightToLeft:right-to-left', 'isReadOnly:is-read-only', 'showPreviewModeClass:is-preview-mode'],
  attributeBindings: ['dir'],
  isDebugMode: false,
  hasSubstantialContent: false,
  // exposed to outside world; has content that should enable the 'Send' button (i.e. excluding markers and blank spaces)
  hasAnyContent: false,
  // only for internal composer use; has any content including blank spaces
  hasNoContent: not('hasAnyContent'),
  hasPlaceholderAndNoContent: and('placeholder', 'hasNoContent'),
  composerState: reads('editorView.composerState'),
  isTypeAheadVisible: false,
  isDragAndDropEnabled: false,
  isDragging: false,
  scrollOffset: 0,
  editorTop: 0,
  hoverCoordinates: reads('editorView.hoverCoordinates'),
  helpGuideIsVisible: false,
  actionReceiver: null,
  dir: 'ltr',
  isFirefox: platform.get('isFirefox'),
  isRightToLeft: equal('dir', 'rtl'),
  ajaxService: service(),
  setup: Em.on('init', function () {
    this.set('uploadManager', UploadManager.create({
      config: {
        appId: this.get('app.id')
      },
      startedUploading: this.get('startedUploading'),
      finishedUploading: this.get('finishedUploading'),
      errorUploading: this.get('errorUploading'),
      ajaxService: this.get('ajaxService')
    }));
    this.configure();
    if (this.hasOnFileDroppedAction()) {
      this.set('isDragAndDropEnabled', true);
    }
    this.initComposerState();
  }),
  initComposerState() {
    let composerState = ComposerState.create({
      blocks: this.get('blocks'),
      attributes: this.get('combinedAttributes'),
      config: this.get('config'),
      uploadManager: this.get('uploadManager'),
      didChange: () => {
        this.send('change');
      }
    });
    composerState.checkpoint();
    this.set('composerState', composerState);
  },
  startedUploading() {},
  finishedUploading() {},
  errorUploading() {},
  configure: Em.observer({
    dependentKeys: ['preset'],
    fn() {
      if (!this.get('config')) {
        let config = Config.createFromPreset(this.get('preset'));
        config.set('attributes', this.get('combinedAttributes'));
        config.set('customObjectAttributeGroups', this.get('customObjectAttributeGroups'));
        this.set('config', config);
        if (config.get('allowUploads')) {
          this.set('isDragAndDropEnabled', true);
        } else {
          this.set('isDragAndDropEnabled', false);
        }
      } else {
        Config.convertToPreset(this.get('config'), this.get('preset'));
      }
      this.set('config.previewContext', this.get('previewContext'));
    },
    sync: true
  }),
  updatePreviewContext: Em.observer({
    dependentKeys: ['previewContext'],
    fn() {
      if (this.get('config')) {
        this.set('config.previewContext', this.get('previewContext'));
      }
    },
    sync: true
  }),
  recover() {
    this.get('editorView').recover(this.get('blocks'));
    this.send('focus');
  },
  isNoEditMode: or('isPreviewMode', 'isReadOnly'),
  isPreviewMode: false,
  isNoAlignmentMode: not('config.allowAlignment'),
  isNoButtonMode: not('config.allowButton'),
  showPreviewModeClass: Em.computed('isReadOnly', 'isPreviewMode', function () {
    return this.get('isPreviewMode') && !this.get('isReadOnly');
  }),
  resetPreviewMode: Em.observer({
    dependentKeys: ['isReadOnly'],
    fn() {
      if (this.get('isReadOnly')) {
        this.set('isPreviewMode', false);
      }
    },
    sync: true
  }),
  isReadOnly: bool('config.readOnly'),
  didInsertElement() {
    this._super(...arguments);
    let editorId = $('.embercom-composer-editor', this.element).attr('id');
    let editorView = this.get('childViews').filterBy('elementId', editorId)[0];
    $(document).on(`click.focus-${this.get('elementId')}`, bind(this, this._bodyClickHandler));
    this.set('editorView', editorView);
    this.set('uploadManager.rerender', editorView.syncAndUpdateDomAndSelection.bind(editorView));
    this.set('actionReceiver', ComposerActionProxy.create({
      composerComponent: this
    }));
  },
  willDestroyElement() {
    this._super(...arguments);
    this.set('actionReceiver', null);
    this.set('uploadManager.rerender', null);
    $(document).off(`click.focus-${this.get('elementId')}`);
  },
  hasOnFileDroppedAction() {
    return this.get('onFileDropped') !== undefined;
  },
  keyDown(e) {
    this.onKeyDown?.(e);
    if (this.get('isTypeAheadVisible')) {
      this.get('typeAheadCoordinator').send('keyDown', e);
    }
  },
  click(e) {
    let url = this._extractUrlFromEvent(e);
    if (url) {
      e.preventDefault();
      this.openUrl(url);
    }
  },
  drop() {
    this.set('isDragging', false);
  },
  dragOver() {
    if (this.get('isDragAndDropEnabled')) {
      this.set('isDragging', true);
    }
  },
  dragLeave() {
    this.set('isDragging', false);
  },
  _isEditorAnchor($targetAnchor) {
    let isValidLink = false;
    let url = $targetAnchor.attr('href');
    let $composerEditor = $targetAnchor.closest('.embercom-composer-editor');
    if (url) {
      isValidLink = url.length && url !== '#';
    }
    return isValidLink && $composerEditor.length;
  },
  _getBlockLink($target) {
    let $composerEditor = $target.closest('.embercom-composer-editor');
    let $targetBlock = $target.closest('ic-block');
    let blockLink = $targetBlock.data('link-url');
    if (blockLink && blockLink.length && $composerEditor.length) {
      return blockLink;
    }
  },
  _extractUrlFromEvent(e) {
    let $target = $(e.target);
    let $targetAnchor = $target.closest('a');
    let url = this._getBlockLink($target);
    if (this.get('isPreviewMode') && !$targetAnchor.length && !url) {
      this.set('isPreviewMode', false);
      return;
    }
    if (this._isEditorAnchor($targetAnchor)) {
      url = $targetAnchor.attr('href');
    }
    return url;
  },
  openUrl(url) {
    let htmlCleaner = new HtmlCleaner();
    if (htmlCleaner.isJavascriptOrDataUri(url)) {
      return;
    }
    if (this.get('isNoEditMode')) {
      let newTab = window.open('');
      newTab.opener = null;
      /* @pat - It's okay to disable this lint rule here as we sanitizing the URL rather than the HTML */
      /* eslint-disable no-unsanitized/method */
      newTab.document.write(`<META HTTP-EQUIV="refresh" content="0; url=${encodeURI(sanitizeUrl(url))}">`);
      newTab.document.close();
    }
  },
  _bodyClickHandler(e) {
    let outerContainerSelector = this.get('outerContainerSelector');
    if (Em.isPresent(outerContainerSelector)) {
      let $target = $(e.target);
      let isContainerButNotEditor = $target.closest(outerContainerSelector).length && !$target.closest('#composer-content').length;
      if (isContainerButNotEditor && !this.get('editorView.uiSelection').isRange()) {
        let clickIsWithinHorizontalBounds = e.pageX > this.get('editorView.left') && e.pageX < this.get('editorView.left') + this.get('editorView.width');
        let clickIsBelowEditorView = e.pageY > this.get('editorView.top') + this.get('editorView.height');
        let clickIsNotInAWidget = $target.closest('.customisable-widget').length === 0;
        if (clickIsWithinHorizontalBounds && clickIsBelowEditorView && clickIsNotInAWidget) {
          this.send('focus', true);
        }
      }
    }
  },
  getBlocks() {
    this.syncEditorView();
    return this.get('composerState.serialized');
  },
  getHtml() {
    this.syncEditorView();
    return this.get('composerState.html');
  },
  syncEditorView() {
    this.get('composerState').syncLock();
    this.get('editorView').syncCurrentBlock(false, true);
    this.get('composerState').syncUnlock();
  },
  load(blocks) {
    this.get('editorView').load(blocks);
    this.get('editorView').checkForContent();
  },
  sendChangeAction() {
    if (this.isDestroying) {
      return true;
    }
    this.onChange?.();
  },
  actions: {
    typeaheadVisiblityChanged(isVisible) {
      this.set('isTypeAheadVisible', isVisible);
    },
    updateDomAndSelection() {
      this.get('editorView').updateDomAndSelection();
    },
    updateDomIfNotFocused() {
      this.get('editorView').updateDomIfNotFocused();
    },
    format(actionName) {
      this.get('editorView').format(actionName);
    },
    insertBlock(block, options) {
      this.get('editorView').insertBlock(block, options);
    },
    insertBlocks(blocks) {
      this.get('editorView').insertBlocks(blocks);
    },
    insertBlocksAtMarker(blocks, markerId) {
      this.syncEditorView();
      let editorView = this.get('editorView');
      editorView.insertBlocksAtMarker(blocks, markerId);
      editorView.checkForContent();
    },
    createBlock(type, data) {
      this.get('editorView').createBlock(type, data);
    },
    enableHoverUpdates() {
      this.set('editorView.disableHoveredBlockUpdates', false);
    },
    disableHoverUpdates() {
      this.set('editorView.disableHoveredBlockUpdates', true);
    },
    getTriggerCoords(triggerCharacter) {
      let $editor = this.get('editorView.$editor');
      let domSelection = DomSelection.create();
      return {
        closestCoords: domSelection.getCoordsOfClosest($editor, triggerCharacter),
        editorCoords: $editor[0].getBoundingClientRect()
      };
    },
    toggleDirection() {
      if (this.get('config.allowRtl')) {
        let newDir = this.get('dir') === 'rtl' ? 'ltr' : 'rtl';
        this.set('dir', newDir);
      }
    },
    formatText(style) {
      let editorView = this.get('editorView');
      editorView.format(style);
      editorView.updateDomAndSelection();
    },
    load(blocks) {
      let editorView = this.get('editorView');
      editorView.load(blocks);
      editorView.checkForContent();
    },
    loadAndFocus(blocks) {
      let editorView = this.get('editorView');
      editorView.load(blocks, true);
      editorView.checkForContent();
    },
    paste(string, type) {
      if (type === 'html' || Em.isEmpty(type)) {
        this.get('editorView').pasteHtml(string);
      } else if (type === 'plain') {
        this.get('editorView').pastePlain(string);
      } else {
        throw new Error('Invalid paste type');
      }
      this.get('editorView').checkForContent();
    },
    onFileDroppedOnEditor(files) {
      this.onFileDropped?.(files);
    },
    removeFocus() {
      this.get('editorView').removeFocus();
    },
    focus(forceFocusAtTheEnd) {
      this.get('editorView').focusAndPosition(true, forceFocusAtTheEnd);
    },
    showHelpGuide() {
      this.set('helpGuideIsVisible', true);
    },
    insertAttribute(attribute) {
      this.get('editorView').insertAttribute(attribute);
    },
    uploadImage(images) {
      this.get('editorView').uploadImages(images);
    },
    uploadVideo(videos) {
      this.get('editorView').uploadVideos(videos);
    },
    uploadAttachment(attachments) {
      this.get('editorView').uploadAttachments(attachments);
    },
    insertVideo(videoUrl) {
      this.get('editorView').insertVideo(videoUrl);
    },
    updatePosition() {
      this.get('editorView').setEditorDimensions();
    },
    reposition() {
      console.debug('Reposition is deprecated. Use updatePosition instead');
      this.send('updatePosition');
    },
    onComposerReady() {
      let $composer = $('.embercom-composer-editor', this.element);
      if ($composer) {
        this.set('composerWidth', $composer.outerWidth());
        let editorView = this.get('editorView');
        if (editorView) {
          editorView.setEditorDimensions();
        }
      }
    },
    triggerOnError(error) {
      error.version = this.get('version');
      error.blocks = JSON.stringify(this.get('blocks'));
      error.selection = this.get('composerState.selectionInfo');
      if (this.get('isDebugMode')) {
        console.error(error);
      }
      if (this.get('config.recoverErrors')) {
        try {
          this.recover();
          error.recovered = true;
          console.log('the composer recovered from an error', error);
        } catch (e) {
          error.recovered = false;
          error.recoverError = e;
        }
      }
      this.set('lastError', error);
      this.onError?.(error);
      this.get('composerState').resetSyncLock();
    },
    focusOut() {
      let onFocusOut = this.get('onFocusOut');
      if (onFocusOut) {
        onFocusOut();
      }
    },
    change() {
      if (!this.get('composerState.isSyncLocked')) {
        scheduleOnce('afterRender', this, this.sendChangeAction);
      }
    },
    typeAheadWasInserted(params) {
      if (this.isDestroying) {
        return;
      }
      this.onTypeAheadInserted?.(params);
    },
    scrollToAttachments() {
      schedule('afterRender', this, function () {
        let additionalPadding = 80;
        let animationTime = 500;
        let $attachmentList = $('.composer-attachment-list', this.element);
        if ($attachmentList.length) {
          let attachmentListBottom = $attachmentList[0].getBoundingClientRect().bottom;
          let $viewport = $attachmentList.scrollParent();
          let viewportBottom;
          if ($viewport.is(document)) {
            viewportBottom = window.innerHeight;
          } else {
            viewportBottom = $viewport[0].getBoundingClientRect().bottom;
          }
          if (attachmentListBottom + additionalPadding > viewportBottom) {
            let $scrollElement = $viewport;
            if ($viewport.is(document)) {
              $scrollElement = $viewport.find('body, html');
            }
            let newScrollTop = $viewport.scrollTop() + (attachmentListBottom - viewportBottom) + additionalPadding;
            $scrollElement.animate({
              scrollTop: newScrollTop
            }, animationTime);
          }
        }
      });
    },
    emailTemplateDataDidChange(identifier, value) {
      if (this.isDestroying) {
        return;
      }
      this.emailTemplateDataDidChange?.(identifier, value);
    }
  }
});